import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_ROW_HEIGHT = 45;
const OVERSCAN = 25;
const SCROLL_CONTAINER_ID = 'scroll-container';

type VirtualRange = {
  start: number;
  end: number;
};

type UseVirtualizedRowsResult = {
  visibleRange: VirtualRange;
  topSpacerHeight: number;
  bottomSpacerHeight: number;
  rowHeight: number;
};

/**
 * @param tbodyRef - Ref to this table's <tbody>. Used to measure the tbody's position within the
 *   scroll container so the visible range is correct even when content precedes this table
 *   (e.g. grouped/sequence view with multiple tables in one scroll container).
 */
function useVirtualizedRows(
  totalRows: number,
  tbodyRef: RefObject<HTMLTableSectionElement | null>
): UseVirtualizedRowsResult {
  const [visibleRange, setVisibleRange] = useState<VirtualRange>({ start: 0, end: Math.min(totalRows, 50) });
  const rowHeightRef = useRef(DEFAULT_ROW_HEIGHT);
  const measuredRef = useRef(false);

  // Measure actual row height from the DOM once, then reuse for all calculations.
  const measureRowHeight = useCallback((): void => {
    if (measuredRef.current) return;
    const tbody = tbodyRef.current;
    if (!tbody) return;
    const firstRow = tbody.querySelector<HTMLElement>("tr[role='row']");
    if (firstRow) {
      const rect = firstRow.getBoundingClientRect();
      if (rect.height > 0) {
        rowHeightRef.current = rect.height;
        measuredRef.current = true;
      }
    }
  }, [tbodyRef]);

  // Determine which rows fall within the viewport using the tbody's position relative to the
  // scroll container, so the math works regardless of how much content sits above this table.
  const computeRange = useCallback((): void => {
    const container = document.getElementById(SCROLL_CONTAINER_ID);
    const tbody = tbodyRef.current;
    if (!container || !tbody || totalRows === 0) return;

    measureRowHeight();

    const rh = rowHeightRef.current;
    const containerRect = container.getBoundingClientRect();
    const tbodyRect = tbody.getBoundingClientRect();

    // How far into the tbody the viewport's top edge is. Positive means some rows are scrolled past.
    const scrollIntoTbody = Math.max(0, containerRect.top - tbodyRect.top);
    const viewportHeight = containerRect.height;

    const rawStart = Math.floor(scrollIntoTbody / rh) - OVERSCAN;
    const rawEnd = Math.ceil((scrollIntoTbody + viewportHeight) / rh) + OVERSCAN;

    const start = Math.max(0, rawStart);
    const end = Math.min(totalRows, rawEnd);

    // Only update state if the range actually changed to avoid unnecessary renders.
    setVisibleRange((prev) => {
      if (prev.start === start && prev.end === end) return prev;
      return { start, end };
    });
  }, [totalRows, measureRowHeight, tbodyRef]);

  // Attach a scroll listener that recalculates the range, throttled to one rAF per frame.
  useEffect(() => {
    const container = document.getElementById(SCROLL_CONTAINER_ID);
    if (!container) return;

    // Re-measure on mount in case row height changed (e.g. column layout shift).
    measuredRef.current = false;
    computeRange();

    let rafId: number | null = null;
    const onScroll = (): void => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        computeRange();
        rafId = null;
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return (): void => {
      container.removeEventListener('scroll', onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [computeRange]);

  // Recompute when total row count changes (e.g. infinite scroll loads more data).
  useEffect(() => {
    computeRange();
  }, [totalRows, computeRange]);

  // Spacer heights replace unrendered rows so the scrollbar and scroll position stay accurate.
  const rh = rowHeightRef.current;

  return {
    visibleRange,
    topSpacerHeight: visibleRange.start * rh,
    bottomSpacerHeight: Math.max(0, (totalRows - visibleRange.end) * rh),
    rowHeight: rh,
  };
}

export default useVirtualizedRows;
